import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';

import { Formatters, Messages } from '../../codegen/Localize';
import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { serverScheduleUrl, siteScheduleUrl } from '../../schedule/utils/ScheduleUtils';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { ISubscriptionInfo, SubscriptionTargetTypes } from '../../ts/models/ISubscriptionInfo';
import { ISiteNameWithId } from '../../ts/ServerApi/types/ISiteNameWithId';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ServerUrls } from '../ServerUrls';
import { SpacesSiteUrls } from '../spaces/SpacesSiteUrls';

export module SubscriptionGrid {

  interface SubscriptionGridColumnOptions {
    translate: IReactTranslateService;
    scope: DataGridCells.ISelectedItemsScope<ISubscriptionInfo>;
    actions: IActionButton<ISubscriptionInfo, {}>[];
    getActionTarget: () => IActionTarget<ISubscriptionInfo, {}>;
    isServerPage: boolean;
    serverUrls: ServerUrls;
    siteUrls: SiteUrls;
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter;
    showUserNameColumn: boolean;
    showWorkbookViewColumn: boolean;
    showScheduleColumn: boolean;
    showSiteColumn: boolean;
    suspendedSupportUrl: string;
  }

  export function columns(columnOptions: SubscriptionGridColumnOptions): DataGrid.Column[] {

    const {
      translate,
      scope,
      actions,
      getActionTarget,
      isServerPage,
      serverUrls,
      siteUrls,
      scheduleFrequencyDescriptionFilter,
      showUserNameColumn,
      showWorkbookViewColumn,
      showScheduleColumn,
      showSiteColumn,
      suspendedSupportUrl
    } = columnOptions;

    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope, (row: DataGrid.Row) => `subscription-checkbox-${row.data.id}`)
    ];

    const subscriptionOnExtractRefreshFn = (row: DataGrid.Row) => {
      const subscription = <ISubscriptionInfo>row.data;
      return FeatureFlags.subscriptionsOnExtractRefreshEnabled && subscription.refreshExtractTriggered;
    };

    if (showUserNameColumn) {
      columns.push({
        name: 'user-col',
        dataKey: 'userName',
        headerText: translate('column_task_user'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
          const subscription = <ISubscriptionInfo>row.data;
          return {
            text: subscription.user.displayName,
            url: siteUrls.forSiteName(subscription.site.urlName).userContentUrl(subscription.user.username, subscription.user.domainName),
            iconClassName: 'tb-grid-icon-user',
            iconTitle: translate('user')
          };
        })
      });
    }

    if (showWorkbookViewColumn) {
      columns.push({
        name: 'workbook-view-col',
        dataKey: 'targetName',
        headerText: translate('column_task_workbook_view'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: targetLinkFormatter(siteUrls, translate)
      });
    } else {
      columns.push({
        name: 'subscription-col',
        dataKey: 'targetName',
        headerText: translate('column_task_subscription'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: targetLinkFormatter(siteUrls, translate)
      });
    }

    columns.push(CommonDataGridColumns.withExtraSpace({
      name: 'subject-col',
      dataKey: 'subject',
      headerText: translate('column_task_subject'),
      headerFormatter: SortableHeader.Formatter(),
      direction: 'asc'
    }));


    if (showScheduleColumn) {

      const scheduleDescriptionFn = (row: DataGrid.Row) => {
        const subscription = <ISubscriptionInfo>row.data;
        const schedule = subscription.schedule;

        return isServerPage
          ? serverScheduleUrl(serverUrls, schedule.id, schedule.scheduledAction)
          : siteScheduleUrl(siteUrls.forSiteName(subscription.site.urlName), schedule.id, schedule.scheduledAction);
      };

      const subscriptionConditionFn = (row: DataGrid.Row) => {
        const subscription = <ISubscriptionInfo>row.data;
        return subscription.canSpecifyDataCondition && subscription.dataConditionPresent;
      };

      columns.push({
        name: 'schedule-name-col-data-present',
        dataKey: 'scheduleName',
        direction: 'asc',
        headerText: translate('column_task_schedule'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.ScheduleDescriptionCellWithIcon(subscriptionConditionFn, translate, scheduleDescriptionFn, scheduleFrequencyDescriptionFilter, subscriptionOnExtractRefreshFn)
      });
    }

    if (showSiteColumn) {
      columns.push({
        name: 'site-name-col',
        dataKey: 'siteName',
        direction: 'asc',
        headerText: translate('column_task_site'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('site',
          (site: ISiteNameWithId) => site.name)
      });
    }

    columns.push({
      name: 'creator-name-col',
      dataKey: 'creatorName',
      direction: 'asc',
      headerText: translate('column_task_subscribed_by'),
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
        const subscription = <ISubscriptionInfo>row.data;
        return {
          text: subscription.creator.displayName,
          url: siteUrls.forSiteName(subscription.site.urlName).userContentUrl(subscription.creator.username, subscription.creator.domainName)
        };
      })
    });

    if (FeatureFlags.suspendSubscriptions) {
      const lastUpdateCell = DataGridCells.InteractiveIconTooltipCell((row: DataGrid.Row) => {
        const subscription = <ISubscriptionInfo>row.data;
        const dateTime = subscription.lastSentAt;
        const dateTimeText = dateTime ? Formatters.formatMediumDateTimeWithoutSeconds(dateTime) : Messages.never();
        if (subscription.suspended) {
          // edge case: subscription was suspended but there is no last sent at date
          const subscriptionMessage = dateTime ? Messages.subscriptionSuspended({ SUSPENDED_DATE: dateTimeText }) : Messages.suspended();
          return {
            text: subscriptionMessage,
            title: Messages.subscriptionTooltipTitle(),
            iconClass: 'tb-react-dg-hcol tb-icon-server-alert-list-suspended',
            tooltipText: 'subscriptionSuspendedMessage',
            tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
            tooltipLinkText: Messages.learnMore(),
            tooltipLink: suspendedSupportUrl
          };
        } else if (subscription.failed) {
          return {
            text: Messages.subscriptionFailed({ FAILED_DATE: dateTimeText }),
            title: Messages.subscriptionTooltipTitle(),
            iconClass: 'tb-react-dg-hcol tb-icon-dialogs-status-alert',
            tooltipText: 'subscriptionFailedMessage',
            tooltipReplacements: { ERROR_MESSAGE: () => subscription.lastRunError ? subscription.lastRunError : '' },
            tooltipLinkText: Messages.learnMore(),
            tooltipLink: suspendedSupportUrl
          };
        } else {
          // a non-failed, non-suspended subscription
          return {
            text: dateTimeText,
            title: Messages.subscriptionTooltipTitle(),
          };
        }
      }, translate);

      columns.push({
        name: 'last-sent-at-col',
        dataKey: 'lastSentAt',
        headerText: translate('column_task_last_update'),
        direction: 'desc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: lastUpdateCell
      });
    } else {
      columns.push({
        name: 'last-sent-at-col',
        dataKey: 'lastSentAt',
        headerText: translate('column_task_last_update'),
        direction: 'desc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.DateTimeCell('lastSentAt', translate('never'))
      });
    }

    columns.push({
      name: 'next-run-at-col',
      dataKey: 'runNextAt',
      headerText: translate('column_task_next_update'),
      direction: 'asc',
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: DataGridCells.SubscriptionNextUpdateDescription()
    });

    // Add the actions menu to whichever column was first (not including the 'selected' column)
    columns[1] = CommonDataGridColumns.withActions(columns[1], {
      actions: actions,
      getActionTarget: getActionTarget,
      translate: translate
    }, scope);

    return columns;
  }

  function targetLinkFormatter(siteUrls: SiteUrls, translate: IReactTranslateService) {

    return DataGridCells.Link((row: DataGrid.Row) => {
      const subscription = row.data as ISubscriptionInfo;
      return getTargetLinkMetadata(subscription);
    });
  }

  export function getTargetLinkMetadata(subscription: ISubscriptionInfo) {
    const siteUrlsForRow = new SpacesSiteUrls({ siteUrlName: subscription.site.urlName });
    let iconClassName: string;
    let iconTitle: string;
    let url: string;

    if (subscription.targetType === SubscriptionTargetTypes.view) {
      if (subscription.attachImage && subscription.attachPdf) {
        iconClassName = 'tb-grid-icon-view-attached-all';
        iconTitle = Messages.subscriptionFormatViewImagePdf();
      } else if (subscription.attachPdf) {
        iconClassName = 'tb-grid-icon-view-attached-pdf';
        iconTitle = Messages.subscriptionFormatViewPdf();
      } else {
        iconClassName = 'tb-grid-icon-view';
        iconTitle = Messages.subscriptionFormatViewImage();
      }

      url = siteUrlsForRow.viewUrl(subscription.targetPath);
    } else {
      if (subscription.attachImage && subscription.attachPdf) {
        iconClassName = 'tb-grid-icon-workbook-attached-all';
        iconTitle = Messages.subscriptionFormatWorkbookImagePdf();
      } else if (subscription.attachPdf) {
        iconClassName = 'tb-grid-icon-workbook-attached-pdf';
        iconTitle = Messages.subscriptionFormatWorkbookPdf();
      } else {
        iconClassName = 'tb-grid-icon-workbook';
        iconTitle = Messages.subscriptionFormatWorkbookImage();
      }

      url = siteUrlsForRow.workbookUrl(subscription.targetId);
    }

    return {
      text: subscription.targetName,
      url: url,
      iconClassName: iconClassName,
      iconTitle: iconTitle
    };
  }
}
